import { useState } from 'react';
import axios from 'api/axiosInstance';
import endpoints from '../api/endpoints';
import {
  Box, Card, Typography, Button, Stack, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, Chip, Divider,
  FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import {
  CloudUploadOutlined,
  CheckCircleOutlined,
  SwapOutlined,
  FileExcelOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UserAddOutlined,
  WarningOutlined
} from '@ant-design/icons';

// Target fields the user can map to
const TARGET_FIELDS = [
  { key: 'patient_name', label: 'Patient Name (Required)', required: true },
  { key: 'phone', label: 'Phone Number (Required)', required: true },
  { key: 'date', label: 'Appointment Date (Required)', required: true },
  { key: 'hospital_id', label: 'Patient ID (Optional)', required: false },
  { key: 'email', label: 'Email (Optional)', required: false },
  { key: 'doctor_name', label: 'Doctor Name', required: false },
  { key: 'specialization', label: 'Specialization', required: false },
  { key: 'status', label: 'Status', required: false },
  { key: 'notes', label: 'Notes', required: false },
];

export default function ImportBookings() {
  const [step, setStep] = useState(1); // 1=upload, 2=map, 3=result
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [createDoctors, setCreateDoctors] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [showDoctorConfirm, setShowDoctorConfirm] = useState(false);
  const [validating, setValidating] = useState(false);

  // Auto-detect mapping for email and date columns
  const autoDetectMapping = (cols) => {
    const mapping = {};
    for (const col of cols) {
      const lower = col.toLowerCase();
      if (lower.includes('email') && !Object.values(mapping).includes('email')) {
        mapping[col] = 'email';
      } else if ((lower.includes('date') || lower.includes('time') || lower.includes('appointment')) && !Object.values(mapping).includes('date')) {
        mapping[col] = 'date';
      } else if ((lower.includes('hospital') || lower.includes('patient_id') || lower.includes('patient id') || lower.includes('registration') || lower.includes('mrn')) && !Object.values(mapping).includes('hospital_id')) {
        mapping[col] = 'hospital_id';
      } else if ((lower.includes('name') || lower.includes('patient')) && !Object.values(mapping).includes('patient_name')) {
        mapping[col] = 'patient_name';
      } else if ((lower.includes('phone') || lower.includes('mobile')) && !Object.values(mapping).includes('phone')) {
        mapping[col] = 'phone';
      } else if ((lower.includes('doctor') || lower.includes('dr')) && !Object.values(mapping).includes('doctor_name')) {
        mapping[col] = 'doctor_name';
      } else if ((lower.includes('specializ') || lower.includes('specialty') || lower.includes('department')) && !Object.values(mapping).includes('specialization')) {
        mapping[col] = 'specialization';
      } else if (lower.includes('status') && !Object.values(mapping).includes('status')) {
        mapping[col] = 'status';
      } else if (lower.includes('note') && !Object.values(mapping).includes('notes')) {
        mapping[col] = 'notes';
      }
    }
    return mapping;
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    const name = selected.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setError('Please upload a CSV or Excel (.xlsx) file');
      return;
    }
    setFile(selected);
    setError('');
  };

  const handleUploadPreview = async () => {
    if (!file) return;
    setLoading(true);
    setError('');

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(endpoints.importBookings.preview, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setColumns(response.data.columns);
      setPreviewRows(response.data.preview_rows);
      const detected = autoDetectMapping(response.data.columns);
      setFieldMapping(detected);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to preview file');
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (csvCol, targetField) => {
    setFieldMapping(prev => {
      const updated = { ...prev };
      // Remove any existing mapping to this target
      if (targetField) {
        for (const key of Object.keys(updated)) {
          if (updated[key] === targetField) delete updated[key];
        }
        updated[csvCol] = targetField;
      } else {
        delete updated[csvCol];
      }
      return updated;
    });
  };

  const isMappingValid = () => {
    const mapped = Object.values(fieldMapping);
    // Name + Phone + Date are required. ID and Email are optional.
    return mapped.includes('date') && mapped.includes('patient_name') && mapped.includes('phone');
  };

  // Check which column is mapped to email/date (these are locked)
  const getLockedTarget = (csvCol) => {
    const target = fieldMapping[csvCol];
    if (target === 'patient_name' || target === 'phone' || target === 'date') return target;
    return null;
  };

  const handleValidateAndImport = async () => {
    if (!file || !isMappingValid()) return;

    // If doctor column is mapped, validate first to check for new doctors
    if (hasDoctorMapping) {
      setValidating(true);
      setError('');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('field_mapping', JSON.stringify(fieldMapping));

      try {
        const response = await axios.post(endpoints.importBookings.validate, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        setValidationData(data);

        if (data.new_doctors.length > 0 || data.multi_spec_doctors.length > 0 || (data.mismatched_doctors && data.mismatched_doctors.length > 0)) {
          // Show confirmation dialog
          setShowDoctorConfirm(true);
          setValidating(false);
          return;
        }
        // No new doctors — proceed directly
        setValidating(false);
        await executeImport();
      } catch (err) {
        setError(err.response?.data?.error || 'Validation failed');
        setValidating(false);
      }
    } else {
      await executeImport();
    }
  };

  const executeImport = async (shouldCreateDoctors = false) => {
    setLoading(true);
    setError('');
    setShowDoctorConfirm(false);

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('field_mapping', JSON.stringify(fieldMapping));
    formData.append('create_doctors', shouldCreateDoctors ? 'true' : 'false');

    try {
      const response = await axios.post(endpoints.importBookings.execute, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = () => {
    executeImport(true);
  };

  const handleSkipNewDoctors = () => {
    executeImport(false);
  };

  const handleDownloadTemplate = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(endpoints.importBookings.template, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'appointments_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleReset = () => {
    setStep(1);
    setFile(null);
    setColumns([]);
    setPreviewRows([]);
    setFieldMapping({});
    setResult(null);
    setError('');
    setCreateDoctors(false);
    setValidationData(null);
    setShowDoctorConfirm(false);
  };

  // Check if doctor_name column is mapped
  const hasDoctorMapping = Object.values(fieldMapping).includes('doctor_name');

  return (
    <Box>
      {step === 1 && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            onClick={handleDownloadTemplate}
            sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', '&:hover': { color: 'primary.dark' } }}
          >
            Download Template
          </Typography>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            <FileExcelOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            <Typography variant="h6">Upload Appointment Data</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Upload a CSV or Excel file containing appointment data. You'll map the columns to fields in the next step.
            </Typography>

            {!file ? (
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadOutlined />}
                sx={{ py: 1.5, px: 4, borderStyle: 'dashed' }}
              >
                Choose File (CSV or XLSX)
                <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileSelect} />
              </Button>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip label={file.name} onDelete={() => setFile(null)} />
                <Typography variant="caption" color="text.secondary">
                  ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              </Stack>
            )}

            <Button
              variant="contained"
              onClick={handleUploadPreview}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <SwapOutlined />}
            >
              {loading ? 'Processing...' : 'Preview & Map Fields'}
            </Button>
          </Stack>
        </Card>
      )}

      {/* Step 2: Field Mapping */}
      {step === 2 && (
        <Stack spacing={3}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Map Your Columns</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Map each column from your file to the correct field. Email and Date are required and auto-detected.
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Your Column</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Maps To</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sample Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {columns.map((col) => {
                    const locked = getLockedTarget(col);
                    return (
                      <TableRow key={col}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{col}</Typography>
                        </TableCell>
                        <TableCell>
                          {locked ? (
                            <Chip
                              label={TARGET_FIELDS.find(f => f.key === locked)?.label}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          ) : (
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <Select
                                value={fieldMapping[col] || ''}
                                onChange={(e) => handleMappingChange(col, e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>Skip this column</em>
                                </MenuItem>
                                {TARGET_FIELDS.filter(f => !f.required).map(f => (
                                  <MenuItem
                                    key={f.key}
                                    value={f.key}
                                    disabled={Object.values(fieldMapping).includes(f.key) && fieldMapping[col] !== f.key}
                                  >
                                    {f.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {previewRows[0]?.[col] || '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {!isMappingValid() && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Patient Name, Phone Number, and Date must be mapped. Patient ID and Email are optional.
              </Alert>
            )}

            {/* Doctor creation checkbox removed — now handled via validation dialog */}
          </Card>

          {/* Preview Data */}
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Data Preview (first 5 rows)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {columns.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row, i) => (
                    <TableRow key={i}>
                      {columns.map(col => (
                        <TableCell key={col} sx={{ fontSize: '0.75rem' }}>{row[col] || '—'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleReset} startIcon={<DeleteOutlined />}>
              Start Over
            </Button>
            <Button
              variant="contained"
              onClick={handleValidateAndImport}
              disabled={!isMappingValid() || loading || validating}
              startIcon={(loading || validating) ? <CircularProgress size={16} /> : <CloudUploadOutlined />}
            >
              {validating ? 'Validating...' : loading ? 'Importing...' : 'Import Appointments'}
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Doctor Confirmation Dialog */}
      <Dialog open={showDoctorConfirm} onClose={() => setShowDoctorConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningOutlined style={{ fontSize: 20, color: '#F59E0B' }} />
            <Typography variant="h5" fontWeight={600}>Review Doctors Before Import</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {validationData && (
            <Stack spacing={2}>
              {validationData.new_doctors.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    The following {validationData.new_doctors.length} doctor(s) don't exist in your system:
                  </Typography>
                  <List dense>
                    {validationData.new_doctors.map((doc, i) => (
                      <ListItem key={i} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <UserAddOutlined style={{ color: '#0D9488' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc.name}
                          secondary={doc.specializations_in_file.length > 0
                            ? `Specialization: ${doc.specializations_in_file.join(', ')}`
                            : 'No specialization'}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {validationData.multi_spec_doctors.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Doctors with multiple specializations in file:
                  </Typography>
                  {validationData.multi_spec_doctors.map((doc, i) => (
                    <Typography key={i} variant="body2">
                      <strong>{doc.name}</strong>: {doc.specializations.join(', ')}
                    </Typography>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Only the first specialization encountered per doctor will be assigned.
                  </Typography>
                </Alert>
              )}

              {validationData.mismatched_doctors && validationData.mismatched_doctors.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Specialization mismatch — existing doctor has a different specialization:
                  </Typography>
                  {validationData.mismatched_doctors.map((doc, i) => (
                    <Typography key={i} variant="body2">
                      <strong>{doc.name}</strong>: system has <em>{doc.current_specialization}</em>, file says <em>{doc.file_specializations.join(', ')}</em>
                    </Typography>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    The existing specialization in the system will be kept.
                  </Typography>
                </Alert>
              )}

              {validationData.new_specializations.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  New specializations that will be created: <strong>{validationData.new_specializations.join(', ')}</strong>
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleSkipNewDoctors} variant="outlined" color="inherit">
            Skip New Doctors
          </Button>
          <Button onClick={handleConfirmImport} variant="contained" startIcon={<UserAddOutlined />}>
            Create Doctors & Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step 3: Result */}
      {step === 3 && result && (
        <Card sx={{ p: 4 }}>
          <Stack spacing={3} alignItems="center">
            <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            <Typography variant="h6">Import Complete</Typography>

            <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
              <Box textAlign="center" sx={{ minWidth: 80 }}>
                <Typography variant="h4" color="primary">{result.created}</Typography>
                <Typography variant="caption">Appointments Added</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box textAlign="center" sx={{ minWidth: 80 }}>
                <Typography variant="h4" color="warning.main">{result.skipped}</Typography>
                <Typography variant="caption">Skipped</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box textAlign="center" sx={{ minWidth: 80 }}>
                <Typography variant="h4" color="success.main">{result.users_created}</Typography>
                <Typography variant="caption">Contacts Created</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box textAlign="center" sx={{ minWidth: 80 }}>
                <Typography variant="h4" color="info.main">{result.users_skipped || 0}</Typography>
                <Typography variant="caption">Existing Contacts</Typography>
              </Box>
              {result.doctors_created > 0 && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box textAlign="center" sx={{ minWidth: 80 }}>
                    <Typography variant="h4" color="secondary.main">{result.doctors_created}</Typography>
                    <Typography variant="caption">Doctors Created</Typography>
                  </Box>
                </>
              )}
            </Stack>

            {result.errors && result.errors.length > 0 && (
              <Alert severity="warning" sx={{ width: '100%', maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Warnings:</Typography>
                {result.errors.map((err, i) => (
                  <Typography key={i} variant="caption" display="block">{err}</Typography>
                ))}
              </Alert>
            )}

            <Button variant="contained" onClick={handleReset}>
              Import More Data
            </Button>
          </Stack>
        </Card>
      )}
    </Box>
  );
}
